// src/pages/SignUp.jsx

import { useState } from "react";
import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, getAdditionalUserInfo } from "firebase/auth";
import { auth, db } from "../config/firebaseConfig";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import LanguageSelector from "../components/LanguageSelector";
import useLangNavigate from "../middleware/useLangNavigate";
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'; // Importa os ícones de olho
import { useAuth } from "../context/AuthContext"; // Import useAuth

export default function SignUp() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false); // Novo estado para visibilidade da senha
  const intl = useIntl();
  const navigate = useLangNavigate();
  const { search } = useLocation();
  const currentLang = new URLSearchParams(search).get("lang") || "en";
  const googleProvider = new GoogleAuthProvider();
  const { user, checkingUser } = useAuth(); // Get user and checkingUser from AuthContext

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError(intl.formatMessage({ id: "fillAllFields" }));
      return;
    }

    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);

      await setDoc(doc(db, "users", result.user.uid), {
        email: result.user.email,
        account_created: serverTimestamp(),
      });

    } catch (error) {
      switch (error.code) {
        case "auth/email-already-in-use":
          setError(intl.formatMessage({ id: "emailInUse" }));
          break;
        case "auth/invalid-email":
          setError(intl.formatMessage({ id: "invalidEmail" }));
          break;
        case "auth/weak-password":
          setError(intl.formatMessage({ id: "weakPassword" }));
          break;
        default:
          setError(`${intl.formatMessage({ id: "signUpError" })} (${error.code})`);
          console.error("Sign Up Error:", error);
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const { isNewUser } = getAdditionalUserInfo(result);

      if (isNewUser) {
        await setDoc(doc(db, "users", result.user.uid), {
          email: result.user.email,
          name: result.user.displayName,
          account_created: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Google Sign-In Error:", error.message);
      setError(intl.formatMessage({ id: "googleLoginError" }));
    }
  };

  // Effect to redirect after successful signup (email or Google)
  useEffect(() => {
    // Only redirect if user is logged in and we're done checking their profile
    if (!checkingUser && user) {
      navigate(`/post-login?lang=${currentLang}`);
    }
  }, [user, checkingUser, navigate, currentLang]);

  if (checkingUser) {
    return <div className="loading-spinner">{intl.formatMessage({ id: "loading" })}</div>; // Exibe um spinner enquanto o estado de autenticação está sendo verificado
  }

  return (
    <>
      <div className="authpage-layout">
        <div className="authpage-left">
          <img src="/lugarlogopw.png" alt="LugarAI Logo" />
          <h1 className="hero-title">
            {intl.formatMessage({ id: "sloganTitle" })}
          </h1>
          <p className="hero-subtitle">
            {intl.formatMessage({ id: "sloganText" })}
          </p>
        </div>

        <div className="authpage-right">
          <div className="auth-form">
            <h2>{intl.formatMessage({ id: "signUpTitle" })}</h2>
            <p>{intl.formatMessage({ id: "signUpSubtitle" })}</p>

            {error && <p className="error-message">{error}</p>}

            <form onSubmit={handleSignUp}>
              <input
                id="email"
                name="email"
                type="email"
                placeholder={intl.formatMessage({ id: "email" })}
                autoComplete="email"
                className="input-field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              {/* Novo container para o campo de senha e o botão de alternância */}
              <div className="password-input-container">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"} // Tipo dinâmico
                  placeholder={intl.formatMessage({ id: "password" })}
                  autoComplete="new-password"
                  className="input-field"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle-button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} /> {/* Ícone de olho do Font Awesome */}
                </button>
              </div>

              <button className="auth-button btn-primary" type="submit">
                {intl.formatMessage({ id: "createAccount" })}
              </button>
            </form>

            <button
              type="button"
              className="auth-button dark"
              onClick={handleGoogleLogin}
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google Icon"
                style={{ width: "20px", marginRight: "8px" }}
              />
              {intl.formatMessage({ id: "continueWithGoogle" })}
            </button>

            <p className="text-sm">
              {intl.formatMessage({ id: "alreadyHaveAccount" })}{" "}
              <a href={`/login?lang=${currentLang}`}>
                {intl.formatMessage({ id: "login" })}
              </a>
            </p>
          </div>
        </div>
      </div>

      <footer className="auth-footer">
        <div className="left"><p>{intl.formatMessage({ id: 'footer.rights' })}</p></div>
        <div className="right">
          <div className="footer-language-selector">
            <LanguageSelector />
          </div>
        </div>
      </footer>
    </>
  );
}
